<template>
  <div class="m-b-20">
    <h4>{{ insuranceLabel }}</h4>
    <div class="radio">
      <label>
        <input
          v-model="form.hasInsurance"
          type="radio"
          name="has_insurance"
          data-test="worker-has-insurance"
          :value="true"
          :checked="form.hasInsurance === true"
        />
        {{ isRecruiter ? $t('The staffing agency has :label', { label: insuranceLabel }) : $t('I have :label', { label: insuranceLabel }) }}
        <i class="input-helper"></i>
      </label>
    </div>
    <div class="radio">
      <label>
        <input
          v-model="form.hasInsurance"
          type="radio"
          name="has_insurance"
          data-test="worker-does-not-have-insurance"
          :value="false"
          :checked="form.hasInsurance === false"
        />
        {{ isRecruiter ? $t('The staffing agency does not have :label', { label: insuranceLabel }) : $t('I do not have :label', { label: insuranceLabel }) }}
        <i class="input-helper"></i>
      </label>
    </div>
    <div v-if="!completed" class="radio">
      <label>
        <input
          v-model="form.hasInsurance"
          type="radio"
          name="has_insurance"
          data-test="worker-insurance-not-answered"
          :value="null"
          :checked="form.hasInsurance === null"
        />
        No option selected yet
        <i class="input-helper"></i>
      </label>
    </div>

    <div v-if="form.hasInsurance" class="bgm-lightgray p-20 m-b-20 rounded">
      <!-- eslint-disable-next-line vue/no-v-html -->
      <p class="c-black" v-html="insuranceInstructionText"></p>
      <div class="row">
        <div class="col-sm-6">
          <PickerFileFilebank
            class=""
            :pre-selected-files="form.proofOfInsuranceFiles"
            :use-input-field="true"
            input-name="proof_of_insurance_file_ids[]"
            :default-tags="defaultTags"
          ></PickerFileFilebank>
        </div>
        <div class="col-sm-4">
          <label>Policy limit</label>
          <br />
          <div class="input-group">
            <span class="input-group-addon">{{ currencySymbol }}</span>
            <input
              v-model.number="form.insurancePolicyLimit"
              type="number"
              name="insurance_policy_limit"
              min="1"
              max="99999999"
              class="form-control text-right"
            />
            <span class="input-group-addon">.00</span>
          </div>
        </div>
      </div>
      <div class="m-b-10 m-t-30">
        <WdsAlert info icon :title="$t('Data Sharing Notice')">
          <template #description>
            {{
              $t(
                isRecruiter
                  ? 'Please note that by uploading your staffing agency insurance certificate, you acknowledge and consent to Worksome sharing it with your clients and potential clients for compliance purposes.'
                  : 'Please note that by uploading your insurance certificate, you acknowledge and consent to Worksome sharing it with your clients and potential clients for compliance purposes.',
              )
            }}
          </template>
        </WdsAlert>
      </div>
      <p class="m-t-20 m-b-0 small">
        By uploading a document here, I confirm that the attached files demonstrate legal proof of
        insurance.
      </p>
    </div>
  </div>

  <div class="form-group">
    <div class="col-md-6 col-md-offset-3 text-center">
      <SubmitButton
        data-test="insurance-information-save-button"
        :text="$t('Save')"
        :disabled="!canSave"
        auto-submit
      ></SubmitButton>
    </div>
  </div>
</template>

<script>
import { WdsAlert } from '@worksome/wds-components'
import PickerFileFilebank from '@/components/PickerFileFilebank.vue'
import SubmitButton from '@/components/SubmitButton.vue'

export default {
  name: 'InsuranceFreelancerForm',
  components: {
    PickerFileFilebank,
    SubmitButton,
    WdsAlert,
  },
  props: {
    hasInsurance: {
      type: Boolean,
    },
    insurancePolicyLimit: {
      type: Number,
      default: 0,
    },
    proofOfInsuranceFiles: {
      type: Array,
      default: [],
    },
    completed: {
      type: Boolean,
    },
    insuranceLabel: {
      type: String,
      default: 'Professional Indemnity Insurance',
    },
    currencySymbol: {
      type: String,
      default: '£',
    },
    isRecruiter: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      form: {
        hasInsurance: this.completed ? this.hasInsurance : null,
        insurancePolicyLimit: this.insurancePolicyLimit,
        proofOfInsuranceFiles: this.proofOfInsuranceFiles || [],
      },
    }
  },
  computed: {
    canSave() {
      return this.form.hasInsurance !== null
    },
    defaultTags() {
      return [this.$t('Insurance proof')]
    },
    insuranceInstructionText() {
      const policyLimit = `<strong>${this.$t('policy limit')}</strong>`
      const upload = `<strong>${this.$t('upload')}</strong>`

      const instruction = this.isRecruiter
        ? this.$t('If your staffing agency has :label, please list the :policyLimit and :upload the policy certificates as proof.', {
            label: this.insuranceLabel,
            policyLimit,
            upload,
          })
        : this.$t('If you have :label, please list your :policyLimit and :upload the policy certificate as proof.', {
            label: this.insuranceLabel,
            policyLimit,
            upload,
          })

      const disclaimer = this.isRecruiter
        ? this.$t('Without proof we will regard your staffing agency as not having insurance.')
        : this.$t('Without proof we will regard you as not having insurance.')

      return `${instruction}<br /><span class="small c-gray">${disclaimer}</span>`
    },
  },
}
</script>

<style scoped></style>
